// Banque de réalisations Léman IT. Source de vérité unique, éditable ici.
//
// Elle alimente deux surfaces :
//  - les pages publiques /realisations et /realisations/[slug] (site vitrine) ;
//  - la section "étude de cas" des pages de proposition IA (/[slug]) : l'IA
//    choisit l'entrée la plus proche du brief et rédige les puces "liens avec le
//    projet", elle n'invente ni projet ni chiffre.
//
// Images sous public/images/realisations/ (couvertures) et
// public/images/realisations/logos/ (logos clients).
//
// Version anglaise des textes affichés : realisations-en.ts (pages /en/...).

import { REALISATIONS_EN } from "./realisations-en";

export interface RealisationTestimonial {
  quote: string;
  author: string;
  role: string;
}

export interface RealisationStep {
  title: string;
  body: string;
}

export interface RealisationShot {
  /** Chemin de la capture sous /public. */
  path: string;
  /** Légende affichée sous la capture. */
  caption: string;
}

export interface Realisation {
  /** Identifiant stable (stocké dans data.caseStudy des pages de proposition). */
  id: string;
  /** Segment d'URL de la page publique : /realisations/<slug>. */
  slug: string;
  /** Nom court affiché (cartes, fil d'ariane). */
  name: string;
  /** Étiquette de catégorie (Étude de cas, Refonte, Reprise de projet...). */
  category: string;
  /** Titre accrocheur, affiché en gros. */
  title: string;
  /** Phrase de résumé (le sous-titre en gras des fiches). */
  summary: string;
  /** Client + positionnement. */
  client: string;
  /** Rôle tenu par Léman IT. */
  role: string;
  /** Secteur / positionnement, sert au matching IA avec le brief. */
  sector: string;
  /**
   * Nature du projet (produit avec utilisateurs / automatisation interne /
   * reprise technique...), sert de garde-fou au choix IA : deux projets de
   * nature différente ne sont jamais un bon rapprochement, même si des
   * détails secondaires semblent coller. Voir DEFAULT_CASE_STUDY_PROMPT.
   */
  nature: string;
  /** 2 à 4 phrases : la situation de départ. */
  context: string;
  /** Ce qui a été fait, étape par étape. */
  steps: RealisationStep[];
  /** Ce que ça change concrètement pour le client. */
  outcome: string;
  /** Résultat concret et vérifiable (jamais de métrique inventée). */
  results: string;
  /** Étiquettes courtes (nature du projet / fonctionnalités). */
  tags: string[];
  /** Stack technique principale. */
  stack: string[];
  /** Témoignage client lié au projet, si disponible. */
  testimonial: RealisationTestimonial | null;
  /** Image de couverture sous /public. */
  imagePath: string;
  /** Captures d'écran supplémentaires, réparties au fil de la page complète. */
  screenshots: RealisationShot[];
  /** Logo client sous /public, si disponible. */
  logoPath?: string;
  /** URL de la plateforme en ligne, si publiquement accessible. */
  liveUrl?: string;
  /** Date de publication ISO (tri de la liste). */
  publishedAt: string;
}

/**
 * Réalisation avec ses champs affichés dans la langue de la page (version
 * anglaise : realisations-en.ts). Le nom, la stack et les images ne changent pas.
 */
export function localizeRealisation(r: Realisation, lang: "fr" | "en"): Realisation {
  const en = lang === "en" ? REALISATIONS_EN[r.id] : undefined;
  if (!en) return r;
  return {
    ...r,
    category: en.category,
    title: en.title,
    summary: en.summary,
    client: en.client,
    role: en.role,
    context: en.context,
    steps: en.steps,
    outcome: en.outcome,
    results: en.results,
    tags: en.tags,
    screenshots: r.screenshots.map((shot, i) => ({ ...shot, caption: en.captions[i] ?? shot.caption })),
    testimonial:
      r.testimonial && en.testimonial
        ? { ...r.testimonial, quote: en.testimonial.quote, role: en.testimonial.role }
        : r.testimonial,
  };
}

/** Vrai si le témoignage affiché en anglais est une traduction. */
export function isTestimonialTranslated(r: Realisation, lang: "fr" | "en"): boolean {
  return lang === "en" && !!REALISATIONS_EN[r.id]?.testimonial && !!r.testimonial;
}

export const REALISATIONS: Realisation[] = [
  {
    id: "ezus",
    slug: "export-api-ezus",
    name: "Export API Ezus",
    category: "Outil métier",
    title:
      "Une application Windows qui transforme l'API Ezus en exports CSV et Excel prêts à l'emploi, sans une ligne de code côté utilisateur",
    summary:
      "Les données des agences de voyages vivent dans Ezus : projets, clients, factures. Les en sortir demandait un développeur ou des copier-coller. L'outil le fait désormais en quelques clics.",
    client: "Un cabinet de conseil qui accompagne des agences de voyages équipées d'Ezus",
    role: "Développeur de l'application, de la conception à l'installateur",
    sector:
      "Tourisme / agences de voyages, export de données depuis l'API d'un logiciel métier (Ezus) vers CSV et Excel, application desktop Windows",
    nature:
      "outil métier interne (application desktop d'export de données connectée à une API tierce, pas de produit SaaS avec utilisateurs inscrits)",
    context:
      "Ezus est un logiciel de gestion pour agences de voyages qui expose ses données par une API : projets, clients, fournisseurs, produits, factures clients et fournisseurs, voyageurs, étapes de projet. Cette API est complète mais austère : double authentification, pagination par jeton, quotas stricts, et des champs personnalisés propres à chaque agence. Les équipes métier ne pouvaient pas exploiter ces données seules.",
    steps: [
      {
        title: "Une application Windows autonome",
        body: "Un installateur suffit, sans environnement technique. La connexion utilise l'email, le mot de passe et la clé API Ezus. L'email et la clé peuvent être mémorisés sous forme chiffrée par Windows, le mot de passe n'est jamais conservé. Un mode démonstration permet la prise en main hors connexion.",
      },
      {
        title: "Une découverte dynamique des champs",
        body: "Sur les huit modules, l'application parcourt les enregistrements pour lister les champs standards et les champs personnalisés propres à l'agence, regroupés par nature et traduits en français.",
      },
      {
        title: "Un aperçu avant export",
        body: "Les données s'affichent en cartes lisibles adaptées à chaque module, ou en tableau. Recherche, filtres, plage de dates et exclusion de lignes permettent de préparer l'export, et le rendu progressif tient plusieurs milliers de lignes. Une sélection de modules, de colonnes et de filtres s'enregistre comme modèle et se rejoue en un clic.",
      },
      {
        title: "Un export CSV ou Excel propre",
        body: "CSV en UTF-8 avec BOM pour que les accents s'affichent correctement dans Excel, séparateur point-virgule ou virgule, ou un classeur Excel unique avec un onglet par module. Nom de fichier personnalisable, horodatage ISO 8601 optionnel et historique des exports précédents.",
      },
      {
        title: "Le respect des contraintes de l'API",
        body: "Pagination complète, limites de 10 000 appels par jour et 100 par seconde tenues avec un compteur conservé d'une session à l'autre, et des erreurs remontées clairement, y compris celles que l'API renvoie sous un code de succès.",
      },
    ],
    outcome:
      "Les équipes exportent leurs données sans dépendre d'un développeur. Chaque export est reproductible, propre et prêt pour Excel ou un outil de pilotage. Les quotas de l'API sont respectés par construction, et les identifiants sont protégés.",
    results:
      "8 modules couverts par un même outil, 2 formats d'export (CSV et Excel), 55 tests automatisés.",
    tags: ["Application desktop", "Intégration API", "Export CSV / Excel", "Outil métier"],
    stack: ["Electron", "React 18", "TypeScript", "Node.js", "ExcelJS", "Bottleneck", "Vitest"],
    testimonial: {
      quote:
        "Prestation nickel. Je cherchais un indépendant pour me concevoir un logiciel d'export sous Windows connecté à une API (avec gestion des accès, synchro dynamique des données et filtres par date vers du CSV). Thibault a répondu présent rapidement. Il a été très réactif de bout en bout et a fait preuve d'un grand professionnalisme. Le résultat est conforme à mes besoins et la solution tourne impeccablement. Je suis pleinement satisfait de notre collaboration.",
      author: "dodaviconsulting",
      role: "Client Codeur.com",
    },
    imagePath: "/images/realisations/ezus.png",
    logoPath: "/images/realisations/logos/ezus.png",
    screenshots: [
      {
        path: "/images/realisations/gallery/ezus-1.png",
        caption: "La connexion : email, mot de passe et clé API Ezus, avec un mode démonstration pour la prise en main hors connexion.",
      },
      {
        path: "/images/realisations/gallery/ezus-2.png",
        caption: "La sélection des champs à exporter : champs standards et champs personnalisés de l'agence, modèles réutilisables, format et séparateur au choix.",
      },
      {
        path: "/images/realisations/gallery/ezus-3.png",
        caption: "Fin d'export : un fichier par module, ouvrable directement, et l'historique des exports précédents.",
      },
    ],
    publishedAt: "2026-09-25",
  },
  {
    id: "gold-merchandising",
    slug: "gold-merchandising",
    name: "Gold Merchandising",
    category: "Reprise de projet",
    title:
      "Reprise d'une plateforme SaaS bâtie en no-code : audit de sécurité, fiabilisation et sortie de la dépendance technique",
    summary:
      "Reprendre l'existant sans rien casser de ce qui tournait, fermer les failles critiques, et rendre au client la maîtrise de son code et de son infrastructure.",
    client:
      "Gold Merchandising, agence de merchandising et de pose de mobilier en magasin (cible historique : Nocibé, cosmétique sélective)",
    role: "Développeur fullstack & architecte technique (reprise)",
    sector:
      "SaaS multi-rôles B2B (admin, client, installateur, transporteur), pilotage d'opérations terrain, reprise d'un existant no-code utilisé en production",
    nature: "produit avec utilisateurs (reprise technique d'un existant en production)",
    context:
      "La plateforme de Gold Merchandising, un SaaS multi-rôles pour piloter des missions de pose en magasin, avait été construite sur Lovable par une personne non-développeuse. Elle fonctionnait et était utilisée en production, mais reposait entièrement sur la plateforme no-code, avec une dette technique accumulée et plusieurs failles de sécurité critiques signalées mais jamais vérifiées.",
    steps: [
      {
        title: "Sortie de la plateforme no-code et infrastructure propriétaire",
        body: "Dépôt Git versionné, images Docker et déploiement Coolify multi-environnements (production, staging) sur VPS Contabo, configuration Supabase injectée au démarrage du conteneur. Le client possède désormais son code et son serveur, sans dépendance à un éditeur tiers.",
      },
      {
        title: "Audit de sécurité et correction en production",
        body: "Audit technique complet, puis correction des failles confirmées exploitables sur la base réelle : élévation de privilège (n'importe quel compte connecté pouvait se déclarer administrateur via l'API), lecture ouverte de la table des utilisateurs, fuite des tarifs clients et des marges vers les sous-traitants. Consolidation d'environ 70 policies RLS redondantes héritées du no-code en une règle claire par table et par action.",
      },
      {
        title: "Remise à niveau de la fiabilité",
        body: "Machine à états des missions reconstruite (statut dérivé par déclencheur PostgreSQL), notifications générées par déclencheurs, tâche planifiée pg_cron pour les alertes de documents expirés, procédures stockées transactionnelles pour la préfacturation et la validation client, stockage de fichiers migré vers un bucket S3 privé avec URLs présignées émises côté serveur après contrôle d'accès, mise en place de Sentry.",
      },
      {
        title: "Authentification sans mot de passe",
        body: "Passage à la connexion par lien magique (plus aucun secret réutilisable à voler ou à rejouer) et fonction d'impersonation réservée aux administrateurs pour le support.",
      },
      {
        title: "Fonctionnalité « Update campagne 2026 »",
        body: "Campagnes multi-magasins avec équipes de pose cloisonnées (chaque poseur ne voit que les missions de son équipe), pas à pas de reporting guidé (photos avant/après, PLV, SAV), import du planning depuis Excel (la couleur de chaque ligne détermine l'équipe), interface trilingue FR/DE/EN, accès observateur en lecture seule pour les contacts côté client. Mise en production de la vraie campagne : 109 magasins, 7 équipes.",
      },
    ],
    outcome:
      "Une plateforme qui n'est plus prisonnière d'un éditeur no-code, dont le client possède intégralement le code et l'infrastructure. Des failles de sécurité fermées avant qu'elles ne coûtent cher, en particulier la fuite de marges à des prestataires externes. Une base technique capable d'accueillir de nouvelles campagnes clients sans tout réécrire.",
    results:
      "Une quarantaine de migrations de sécurité et de fonctionnalités livrées et appliquées en production, failles critiques fermées et vérifiées, campagne Nocibé de 109 magasins et 7 équipes mise en ligne, code base assainie (zéro erreur de typage, environ 145 fichiers morts supprimés).",
    tags: ["Reprise de projet", "Audit de sécurité", "Sortie du no-code", "Opérations terrain"],
    stack: [
      "React 18",
      "Vite",
      "TypeScript",
      "Supabase (PostgreSQL, RLS, Edge Functions, pg_cron)",
      "TanStack Query",
      "Tailwind / shadcn",
      "Contabo S3",
      "Sentry",
      "i18next",
      "Docker / Coolify",
    ],
    testimonial: null,
    imagePath: "/images/realisations/gold-merchandising.png",
    screenshots: [
      {
        path: "/images/realisations/gallery/gold-merchandising-1.png",
        caption: "Vue installateur : chaque poseur ne voit que les missions de son équipe, jamais le listing global.",
      },
      {
        path: "/images/realisations/gallery/gold-merchandising-2.png",
        caption: "Le pas à pas de reporting guidé : photos avant/après, PLV, SAV, étape par étape.",
      },
      {
        path: "/images/realisations/gallery/gold-merchandising-3.png",
        caption: "Écran de réception : déclaration de l'état des PLV reçues par magasin.",
      },
    ],
    logoPath: "/images/realisations/logos/gold-merchandising.png",
    publishedAt: "2026-09-10",
  },
  {
    id: "lensmentor",
    slug: "lensmentor",
    name: "LensMentor",
    category: "Étude de cas",
    title:
      "Un SaaS d'apprentissage photo porté par un mentor IA, de l'architecture RAG à la mise en production",
    summary:
      "Traduire une vision pédagogique portée par une experte non-technique en un produit SaaS différencié, avec une architecture IA pensée pour que chaque euro de génération serve la qualité perçue.",
    client:
      "LensMentor, plateforme française d'apprentissage de la photographie (cliente : Marie-Ange Chabroullet, photographe professionnelle)",
    role: "Développeur full-stack & architecte IA",
    sector:
      "SaaS / EdTech, produit propulsé par l'IA pour un public non technique, différenciation par la qualité du mentor plutôt que par un simple accès à un modèle",
    nature: "produit avec utilisateurs (SaaS construit de zéro)",
    context:
      "Marie-Ange voulait transformer son expertise pédagogique en produit SaaS : une plateforme capable de faire progresser des photographes amateurs grâce à un mentor IA donnant de vraies critiques de photos, personnalisées selon le matériel et le niveau de chacun. Le défi n'était pas de brancher un modèle mais de construire une vraie différenciation produit : un mentor qui comprend le contexte de l'élève.",
    steps: [
      {
        title: "Architecture IA sur-mesure",
        body: "Conception de bout en bout du mentor IA, propulsé par Claude (Anthropic). Architecture de prompt caching en couches (système, contenu pédagogique, spécifications du boîtier, contexte utilisateur, historique) pour optimiser à la fois la pertinence des réponses et le coût par requête.",
      },
      {
        title: "RAG et base de connaissances",
        body: "Recherche augmentée sur deux sources (contenu des leçons et historique de critiques photo), indexées avec pgvector sur Neon et des embeddings Voyage AI. Grille de critique structurée combinant critères universels et critères par mode, sur trois paliers de difficulté adaptative.",
      },
      {
        title: "Base de données caméras",
        body: "Base de boîtiers (Canon, Nikon, Sony, Fujifilm, Panasonic, Olympus, Pentax depuis 2015) permettant de personnaliser les conseils selon le matériel réel : réglages ISO, vitesse d'obturation et compensation d'exposition bornés par les capacités du boîtier.",
      },
      {
        title: "Gamification et parcours pédagogique",
        body: "Sept parcours structurés, système d'XP unifié où le niveau déclaré à l'onboarding fixe le point de départ, recommandations hebdomadaires générées automatiquement, garde-fous anti-abus sur les actions à XP.",
      },
    ],
    outcome:
      "Un produit SaaS fonctionnel et différencié, dont l'architecture IA est prête à être enrichie sans explosion des coûts de génération quand l'usage grandit.",
    results:
      "Premiers jalons validés et facturés, architecture IA prête à être enrichie, produit en développement actif.",
    tags: ["SaaS", "Mentor IA (RAG)", "Gamification", "Marketplace"],
    stack: ["Next.js", "Neon (PostgreSQL) + pgvector", "Voyage AI", "Claude API (Anthropic)"],
    testimonial: {
      quote:
        "Thibault a développé de zéro la plateforme SaaS LensMentor et l'expérience a été excellente de bout en bout. D'abord, sa réactivité : les réponses sont rapides et les points réguliers, on ne reste jamais dans l'attente. Ensuite, il est force de proposition. Il ne se contente pas d'exécuter un cahier des charges, il challenge, propose des solutions techniques pertinentes et apporte une vraie valeur ajoutée sur les choix d'architecture. Troisième point fort : d'excellentes compétences en développement, avec un code propre et fiable. Mais ce que j'ai le plus apprécié, c'est son côté humain et sa capacité d'écoute. Il comprend les enjeux d'un projet de fondateur, s'adapte, et rend la collaboration fluide et agréable. Je recommande Thibault sans hésiter et je continue à travailler avec lui.",
      author: "Marie-Ange Chabroullet",
      role: "Fondatrice & CEO, LensMentor",
    },
    imagePath: "/images/realisations/lensmentor.jpg",
    screenshots: [
      {
        path: "/images/realisations/gallery/lensmentor-1.jpg",
        caption: "Le mentor IA : réponses cadrées par le mode choisi et les caractéristiques du boîtier de l'élève.",
      },
      {
        path: "/images/realisations/gallery/lensmentor-2.jpg",
        caption: "Les parcours pédagogiques, découpés en leçons avec progression et paliers de difficulté.",
      },
      {
        path: "/images/realisations/gallery/lensmentor-3.jpg",
        caption: "Le back-office : membres, revenus, activité, suivi des coûts de génération IA.",
      },
    ],
    logoPath: "/images/realisations/logos/lensmentor.png",
    liveUrl: "https://lensmentor.fr",
    publishedAt: "2026-08-05",
  },
  {
    id: "lcde",
    slug: "lcde",
    name: "La Communauté des Entrepreneurs",
    category: "Refonte",
    title:
      "Refonte complète d'une plateforme communautaire : de WordPress à une architecture Next.js sur-mesure avec assistant IA",
    summary:
      "Reprendre un existant WordPress saturé de plugins, comprendre sa logique métier, et le reconstruire sur des bases solides sans jamais perdre le contact avec la réalité business du produit.",
    client:
      "La Communauté des Entrepreneurs, plateforme francophone dédiée à l'entrepreneuriat",
    role: "Développeur fullstack & architecte technique (co-fondateur technique du projet)",
    sector:
      "Plateforme communautaire / EdTech, refonte d'un existant, fondatrice non technique, assistant IA propriétaire",
    nature: "produit avec utilisateurs (refonte complète d'un existant)",
    context:
      "LCDE tournait sur WordPress avec une pile de plugins (BuddyBoss, WooCommerce, plugin chatbot custom). La fondatrice, non-technique, avait besoin d'un partenaire capable de reprendre l'existant, comprendre sa logique métier et le reconstruire sur des bases solides.",
    steps: [
      {
        title: "Audit et extraction de l'existant",
        body: "Avant toute réécriture, dissection du plugin chatbot maison en place (PHP + React) pour en extraire la logique métier réelle : prompt système, déclencheurs, parcours utilisateur. Objectif : ne rien perdre de ce qui fonctionnait déjà.",
      },
      {
        title: "Refonte technique complète",
        body: "Migration de WordPress vers Next.js 14, base Neon (PostgreSQL serverless), Drizzle ORM, paiements Stripe, emailing Brevo, analytics GA4, hébergement Coolify sur VPS dédié.",
      },
      {
        title: "Alfred, l'assistant IA de la plateforme",
        body: "Reconstruction complète du chatbot : passage à Mistral AI, architecture RAG branchée sur trois sources (profil utilisateur, ressources de la plateforme indexées par embeddings, base de connaissances privée), et une identité cohérente avec le ton de la marque.",
      },
      {
        title: "Notifications et parcours utilisateur",
        body: "Système de notifications complet (in-app + email), parcours structuré en neuf étapes pour guider les membres, logique de gamification pour maximiser l'engagement.",
      },
    ],
    outcome:
      "Une plateforme qui n'est plus dépendante d'un empilement de plugins WordPress fragiles, un assistant IA qui connaît réellement le contenu et les membres de la communauté, et une base technique pensée pour évoluer sans repartir de zéro à chaque nouvelle fonctionnalité.",
    results:
      "Plateforme V2 livrée et en production, refonte menée sur plusieurs mois dans les délais.",
    tags: ["Refonte / migration", "Assistant IA (RAG)", "Notifications", "Gamification"],
    stack: [
      "Next.js 14",
      "Neon (PostgreSQL)",
      "Drizzle ORM",
      "Stripe",
      "Brevo",
      "Mistral AI (RAG + embeddings)",
      "Coolify / VPS",
    ],
    testimonial: {
      quote:
        "J'ai confié à Thibault la refonte complète de la V2 de ma plateforme, sur plusieurs mois. Le résultat dépasse mes attentes. Techniquement, ses choix ont été solides à chaque étape, avec un code propre et une vraie maîtrise des technologies. Il a su prendre du recul sur l'architecture, évitant ainsi beaucoup de dette technique. Surtout, son autonomie m'a marquée : jamais besoin de relancer, il anticipait et revenait avec des solutions. Délais tenus. Je recommande Thibault sans hésiter.",
      author: "Emilie Attar",
      role: "Fondatrice et gérante, SARL La Communauté des Entrepreneurs",
    },
    imagePath: "/images/realisations/lcde.jpg",
    screenshots: [
      {
        path: "/images/realisations/gallery/lcde-1.jpg",
        caption: "La bibliothèque de ressources : articles, vidéos, podcasts et documents indexés sur les neuf étapes du parcours.",
      },
      {
        path: "/images/realisations/gallery/lcde-2.jpg",
        caption: "L'espace communauté : fil, forums thématiques, membres actifs et mise en relation.",
      },
      {
        path: "/images/realisations/gallery/lcde-3.jpg",
        caption: "Alfred, l'assistant IA de la plateforme, avec sa propre identité et un RAG branché sur trois sources.",
      },
    ],
    logoPath: "/images/realisations/logos/lcde.jpg",
    liveUrl: "https://app.lacommunautedesentrepreneurs.com",
    publishedAt: "2026-08-05",
  },
  {
    id: "cheeze",
    slug: "cheeze",
    name: "Cheeze",
    category: "Dashboard opérationnel",
    title:
      "Un dashboard qui pilote la performance financière et opérationnelle d'un réseau de plus de 30 cliniques dentaires",
    summary:
      "Une vision consolidée du réseau, clinique par clinique et praticien par praticien, là où les tableurs manuels ne permettaient que des photographies partielles et décalées.",
    client:
      "The Dental Network SA (Cheeze), réseau de plus de 30 cliniques dentaires en Suisse",
    role: "Développeur fullstack",
    sector: "Santé, réseau multi-sites, pilotage de la performance opérationnelle",
    nature: "produit avec utilisateurs (dashboard interne multi-sites)",
    context:
      "Un réseau de plus de 30 cliniques dentaires a besoin de suivre, au même endroit et avec les mêmes chiffres, le revenu presté et facturé, les rabais accordés et la performance de chaque praticien. Sans plateforme dédiée, ce suivi reposait sur des exports et des tableurs recomposés clinique par clinique.",
    steps: [
      {
        title: "Suivi financier consolidé",
        body: "Revenu net et brut presté, facturé, comparés à la moyenne du réseau, agrégés par clinique et par mois, avec l'historique de performance sur plusieurs mois glissants.",
      },
      {
        title: "Analyse des rabais",
        body: "Décomposition des remises accordées par rôle (spécialiste, médecin-dentiste, hygiéniste) et par praticien, pour repérer les dérives tarifaires avant qu'elles ne pèsent sur la marge.",
      },
      {
        title: "Pilotage du taux de remplissage",
        body: "Remplissage des plannings comparé aux équivalents temps plein et aux disponibilités réelles, par clinique, pour objectiver les décisions de recrutement et d'organisation.",
      },
      {
        title: "Performance individuelle des praticiens",
        body: "Chiffre d'affaires par heure ouverte et par heure disponible, atteinte des objectifs, et indicateurs de rendez-vous par praticien, pour un pilotage fin sans ressaisie manuelle.",
      },
    ],
    outcome:
      "Une plateforme unique où chaque clinique du réseau se pilote avec les mêmes indicateurs, à jour, sans recomposer un tableur à chaque fin de mois.",
    results:
      "Dashboard utilisé en continu par le réseau pour piloter son activité, avec un historique de performance qui s'enrichit mois après mois.",
    tags: ["Dashboard ops", "Reporting financier", "Analyse de performance", "Multi-sites"],
    stack: ["React", "Python", "MongoDB"],
    testimonial: {
      quote:
        "Je recommande vivement Thibault pour ses compétences exceptionnelles en programmation. Il se distingue par une excellente capacité d'exécution, alliée à une compréhension fine de l'environnement opérationnel dans lequel s'inscrivent les solutions développées. Au-delà de la qualité technique de son travail, Thibault sait rapidement appréhender les enjeux métiers et proposer des solutions adaptées, robustes et efficaces. Son expérience sur une grande diversité de sujets, tous menés avec succès, témoigne de sa rigueur, de sa fiabilité, de sa polyvalence et de son engagement.",
      author: "Maren Knief-Clerc",
      role: "Knief Clerc Advisory",
    },
    imagePath: "/images/realisations/cheeze.png",
    screenshots: [],
    logoPath: "/images/realisations/logos/cheeze.png",
    publishedAt: "2026-08-25",
  },
  {
    id: "flashposter",
    slug: "flashposter",
    name: "FlashPoster.ai",
    category: "Projet fondateur",
    title: "Un SaaS IA de création de contenu blog, conçu, développé et lancé en solo",
    summary:
      "Porter un produit SaaS seul, de la conception à l'exploitation : décisions d'architecture, développement complet, déploiement et exploitation sans équipe.",
    client: "FlashPoster, outil SaaS propulsé par l'IA pour la création de posts de blog",
    role: "Fondateur & développeur full-stack (projet fondateur, inactif aujourd'hui)",
    sector:
      "SaaS de création de contenu, IA générative, SEO, produit porté de l'idée à la production sans équipe",
    nature: "produit avec utilisateurs (SaaS construit de zéro, projet fondateur solo)",
    context:
      "Produire du contenu blog de qualité à un rythme soutenu demande une expertise rédactionnelle et SEO que beaucoup d'équipes n'ont pas en interne. L'idée de FlashPoster : permettre à n'importe qui de passer de l'inspiration à la publication en trois clics, avec un contenu optimisé pour le référencement.",
    steps: [
      {
        title: "Conception produit et lancement en autonomie",
        body: "De l'idée au produit en production : cadrage du besoin, conception de l'architecture, développement et déploiement portés seul, de bout en bout.",
      },
      {
        title: "Génération de contenu assistée par IA",
        body: "Intégration de l'API OpenAI pour générer des articles de blog optimisés SEO, avec un parcours pensé pour réduire au maximum la friction entre l'idée de départ et l'article publié.",
      },
      {
        title: "Infrastructure de production",
        body: "Backend Python / Flask, frontend React / TypeScript, base MongoDB, déploiement sur VPS avec pipeline CI/CD, une stack pensée pour tenir la charge d'une génération de contenu en continu.",
      },
      {
        title: "Itération sur la performance",
        body: "Optimisation du pipeline de génération pour réduire les temps de traitement, avec un objectif constant de fiabilité de la plateforme en production.",
      },
    ],
    outcome:
      "Une démonstration concrète de la capacité à porter un produit SaaS seul, de la première ligne de code jusqu'à l'exploitation.",
    results:
      "2 500 articles générés et publiés sur WordPress, 99,5 % d'uptime en production, pipeline de génération optimisé d'environ 80 % en rapidité.",
    tags: ["SaaS", "Génération IA", "SEO", "Fondateur solo"],
    stack: ["Python", "Flask", "React", "TypeScript", "MongoDB", "OpenAI API", "VPS", "CI/CD"],
    testimonial: null,
    imagePath: "/images/realisations/flashposter.png",
    screenshots: [
      {
        path: "/images/realisations/gallery/flashposter-1.png",
        caption: "L'éditeur d'article généré : structure, images, métadonnées SEO et export prêts à publier.",
      },
      {
        path: "/images/realisations/gallery/flashposter-2.png",
        caption: "Le paramétrage de génération : gabarit, longueur, liens internes et externes, publication vers le site.",
      },
    ],
    logoPath: "/images/realisations/logos/flashposter.png",
    publishedAt: "2026-08-05",
  },
  {
    id: "prospection-b2b",
    slug: "pipeline-prospection-b2b",
    name: "Pipeline prospection B2B",
    category: "Automatisation",
    title:
      "Un pipeline de prospection B2B automatisé pour cibler 103 000 entreprises, livré en dix jours",
    summary:
      "Prouver qu'un système de prospection à grande échelle peut être livré vite et sans développement lourd, avec une architecture no-code/low-code adaptée à un budget et un délai contraints.",
    client: "Un search fund basé à Genève",
    role: "Architecte automatisation & développeur",
    sector:
      "Search fund / M&A, prospection et enrichissement de données à grande échelle, budget et délai contraints, stack no-code/low-code",
    nature: "automatisation interne (pipeline de données et d'enrichissement, pas de produit avec utilisateurs)",
    context:
      "Un search fund genevois avait besoin d'identifier et de qualifier des cibles d'acquisition parmi les PME du canton, sans budget pour du développement lourd et sans le temps de recruter une équipe data.",
    steps: [
      {
        title: "Constitution de la base cible",
        body: "Extraction et nettoyage d'une base de 103 000 entreprises genevoises à partir des données publiques SITG, avec filtrage par code NOGA pour cibler les secteurs pertinents.",
      },
      {
        title: "Enrichissement multi-sources",
        body: "Pipeline combinant Zefix (registre du commerce suisse), l'API du registre du commerce de Genève via proxy résidentiel suisse, Google Places et LinkedIn, pour compléter chaque fiche entreprise avec des données fiables.",
      },
      {
        title: "Scoring et synthèse par IA",
        body: "Utilisation de Kimi K2.5 pour générer des résumés d'entreprise et des messages de prise de contact personnalisés, remplaçant un travail de qualification manuel impossible à cette échelle.",
      },
      {
        title: "Orchestration et CRM",
        body: "Automatisation de bout en bout avec n8n, données centralisées dans Teable puis Attio (CRM), outreach piloté via Lemlist. Le tout connecté par une architecture pensée pour rester maintenable sans équipe technique interne.",
      },
    ],
    outcome:
      "Un système de prospection à grande échelle livré rapidement, sans sacrifier ni la couverture de la cible ni la qualité de l'enrichissement.",
    results:
      "Pipeline mis en production, capable de traiter une base de 103 000 entreprises, devenu le projet d'automatisation le plus complexe géré par l'agence.",
    tags: ["Data pipeline", "Automatisation n8n", "Enrichissement", "Scoring IA"],
    stack: ["n8n", "Teable", "Attio (CRM)", "Lemlist", "Zefix", "Google Places", "Kimi K2.5"],
    testimonial: null,
    imagePath: "/images/realisations/prospection-b2b.jpg",
    screenshots: [
      {
        path: "/images/realisations/gallery/prospection-b2b-1.jpg",
        caption: "Un des workflows n8n : enrichissement d'un dirigeant à partir de LinkedIn, avec variantes de recherche et analyse par IA.",
      },
      {
        path: "/images/realisations/gallery/prospection-b2b-2.jpg",
        caption: "La base cible centralisée dans Teable : 103 000 entreprises filtrées par code NOGA, prêtes pour l'enrichissement.",
      },
    ],
    publishedAt: "2026-08-05",
  },
];

export function getRealisation(id: string | null | undefined): Realisation | null {
  if (!id) return null;
  return REALISATIONS.find((r) => r.id === id) ?? null;
}

export function getRealisationBySlug(slug: string): Realisation | null {
  return REALISATIONS.find((r) => r.slug === slug) ?? null;
}

/** URL publique de la page complète d'une réalisation (/en/... en anglais). */
export function realisationPath(r: Pick<Realisation, "slug">, lang: "fr" | "en" = "fr"): string {
  return `${lang === "en" ? "/en" : ""}/realisations/${r.slug}`;
}

/**
 * Résumé compact de la banque, injecté dans le prompt IA à la place du
 * placeholder {{realisations}}. On ne passe ni témoignage ni image : seulement
 * ce qui est utile au choix de la réalisation la plus pertinente.
 */
export function buildRealisationsForPrompt(): string {
  return REALISATIONS.map((r) =>
    [
      `### ${r.id}`,
      `Nom : ${r.name}`,
      `Nature du projet : ${r.nature}`,
      `Secteur / positionnement : ${r.sector}`,
      `Résumé : ${r.summary}`,
      `Contexte : ${r.context}`,
      `Ce qui a été fait : ${r.steps.map((s) => s.title).join(" ; ")}`,
      `Tags : ${r.tags.join(", ")}`,
      `Stack : ${r.stack.join(", ")}`,
      `Résultats : ${r.results}`,
    ].join("\n")
  ).join("\n\n");
}
